// components/blog/KofiWidget.jsx
// Floating coffee button (bottom-right). Ko-fi iframe loads on demand when user opens the modal.
import { useState } from "react";
import { X, ExternalLink } from "lucide-react";

const KOFI_USERNAME = "malickecase";
const KOFI_EMBED_URL = `https://ko-fi.com/${KOFI_USERNAME}/?hidefeed=true&widget=true&embed=true&preview=true`;
const KOFI_PAGE_URL  = `https://ko-fi.com/${KOFI_USERNAME}`;

const STYLES = `
@keyframes kofiBtnPop {
  0%   { transform: scale(0.8); opacity: 0; }
  70%  { transform: scale(1.08); }
  100% { transform: scale(1);   opacity: 1; }
}
@keyframes kofiModalIn {
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
}
@keyframes kofiModalOut {
  from { opacity: 1; transform: translateY(0)    scale(1);    }
  to   { opacity: 0; transform: translateY(14px) scale(0.97); }
}
.kofi-btn-pop   { animation: kofiBtnPop   400ms cubic-bezier(0.34,1.56,0.64,1) forwards; }
.kofi-modal-in  { animation: kofiModalIn  260ms cubic-bezier(0.34,1.2,0.64,1)  forwards; }
.kofi-modal-out { animation: kofiModalOut 200ms ease forwards; }
@keyframes kofiSteam {
  0%,100% { transform: translateY(0)    scaleX(1);    opacity: 0.55; }
  50%     { transform: translateY(-5px) scaleX(1.2); opacity: 0.9;  }
}
.kofi-steam-1 { animation: kofiSteam 1.8s ease-in-out infinite; }
.kofi-steam-2 { animation: kofiSteam 1.8s ease-in-out infinite 0.35s; }
`;

export function KofiButton({ isDark }) {
  const [modalPhase,   setModalPhase]   = useState("closed");
  const [redirectWarn, setRedirectWarn] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  function openModal() {
    setModalPhase("open");
  }

  function closeModal() {
    setModalPhase("closing");
    setTimeout(() => setModalPhase("closed"), 210);
  }

  function confirmRedirect() {
    window.open(KOFI_PAGE_URL, "_blank", "noopener,noreferrer");
    setRedirectWarn(false);
    closeModal();
  }

  const bg  = isDark ? "#111114" : "#EDEBE5";
  const bdr = isDark ? "#27272F" : "#C8C4BC";

  return (
    <>
      <style>{STYLES}</style>

      {/* Floating trigger */}
      <button
        onClick={openModal}
        aria-label="Support on Ko-fi"
        title="Support on Ko-fi"
        className="kofi-btn-pop"
        style={{
          position: "fixed", bottom: 24, right: 20, zIndex: 9600,
          width: 52, height: 52, borderRadius: "50%", border: "none",
          background: "var(--blog-accent-teal)", color: "#fff",
          cursor: "pointer", display: "flex", alignItems: "center",
          justifyContent: "center", boxShadow: "0 4px 18px rgba(0,194,168,0.45)",
          fontSize: 22, transition: "transform 150ms, box-shadow 150ms",
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; e.currentTarget.style.boxShadow = "0 6px 24px rgba(0,194,168,0.6)"; }}
        onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)";   e.currentTarget.style.boxShadow = "0 4px 18px rgba(0,194,168,0.45)"; }}
      >
        ☕
      </button>

      {/* Ko-fi modal */}
      {modalPhase !== "closed" && (
        <div
          onClick={closeModal}
          style={{
            position: "fixed", inset: 0, zIndex: 9650,
            background: "rgba(0,0,0,0.45)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "flex-end", justifyContent: "flex-end",
            padding: "0 20px 88px",
          }}
        >
          <div
            className={`blog-theme ${modalPhase === "closing" ? "kofi-modal-out" : "kofi-modal-in"}`}
            data-blog-theme={isDark ? "dark" : "light"}
            onClick={e => e.stopPropagation()}
            style={{
              width: "min(380px, calc(100vw - 40px))",
              background: bg, border: `0.5px solid ${bdr}`,
              borderRadius: 14, overflow: "hidden",
              boxShadow: isDark ? "0 24px 60px rgba(0,0,0,0.8)" : "0 24px 60px rgba(0,0,0,0.16)",
              fontFamily: "'Inter', system-ui, sans-serif",
            }}
          >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px 12px", borderBottom: `0.5px solid ${bdr}`, background: isDark ? "#0a0a0c" : "#E1DDD5" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ position: "relative", width: 28, height: 34 }}>
                  <div style={{ position: "absolute", top: 0, left: 0, right: 0, display: "flex", gap: 3, justifyContent: "center" }}>
                    <div className="kofi-steam-1" style={{ width: 2, height: 7, borderRadius: 99, background: "var(--blog-accent-teal)", opacity: 0.6 }} />
                    <div className="kofi-steam-2" style={{ width: 2, height: 7, borderRadius: 99, background: "var(--blog-accent-teal)", opacity: 0.6 }} />
                  </div>
                  <div style={{ position: "absolute", bottom: 0, fontSize: 20 }}>☕</div>
                </div>
                <div>
                  <div className="font-display font-medium" style={{ fontSize: 14, color: "var(--blog-text-primary)" }}>Support the writing</div>
                  <div className="font-mono uppercase" style={{ fontSize: 9, letterSpacing: "0.10em", color: "var(--blog-text-tertiary)" }}>via Ko-fi · free platform</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button onClick={() => setRedirectWarn(true)} title="Open Ko-fi in new tab"
                  style={{ display: "flex", alignItems: "center", gap: 4, background: "transparent", border: `0.5px solid ${bdr}`, borderRadius: 6, color: "var(--blog-text-tertiary)", fontSize: 10, cursor: "pointer", padding: "5px 9px", fontFamily: "inherit", letterSpacing: "0.04em" }}>
                  <ExternalLink style={{ width: 10, height: 10 }} /> Ko-fi.com
                </button>
                <button onClick={closeModal} aria-label="Close"
                  style={{ background: "transparent", border: "none", color: "var(--blog-text-tertiary)", cursor: "pointer", padding: 4, lineHeight: 1 }}>
                  <X style={{ width: 15, height: 15 }} />
                </button>
              </div>
            </div>

            {/* Iframe */}
            <div style={{ position: "relative", minHeight: 400 }}>
              {!iframeLoaded && (
                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 10, color: "var(--blog-text-tertiary)", fontSize: 12 }}>
                  <span style={{ fontSize: 28 }}>☕</span>
                  <span style={{ opacity: 0.5 }}>Loading Ko-fi...</span>
                </div>
              )}
              <iframe
                src={KOFI_EMBED_URL}
                title="Support on Ko-fi"
                onLoad={() => setIframeLoaded(true)}
                style={{ display: iframeLoaded ? "block" : "none", width: "100%", height: 460, border: "none", background: "transparent" }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Redirect warning */}
      {redirectWarn && (
        <div onClick={() => setRedirectWarn(false)}
          style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div className="blog-theme" data-blog-theme={isDark ? "dark" : "light"}
            onClick={e => e.stopPropagation()}
            style={{ background: bg, border: `0.5px solid ${bdr}`, borderRadius: 14, padding: "28px 28px 24px", maxWidth: 360, width: "100%", textAlign: "center", fontFamily: "'Inter', system-ui, sans-serif" }}>
            <div style={{ fontSize: 36, marginBottom: 14 }}>☕</div>
            <div className="font-display font-medium" style={{ fontSize: 18, color: "var(--blog-text-primary)", marginBottom: 8 }}>Heading to Ko-fi</div>
            <p style={{ fontSize: 12.5, color: "var(--blog-text-secondary)", lineHeight: 1.65, marginBottom: 22 }}>
              You'll be taken to <strong>ko-fi.com</strong>, a trusted platform where creators receive support directly. No middleman, no monthly fee.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button onClick={confirmRedirect}
                style={{ display: "flex", alignItems: "center", gap: 7, padding: "11px 20px", borderRadius: 8, border: "none", background: "var(--blog-accent-teal)", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                <ExternalLink style={{ width: 12, height: 12 }} /> Take me there
              </button>
              <button onClick={() => setRedirectWarn(false)}
                style={{ padding: "11px 20px", borderRadius: 8, border: `0.5px solid ${bdr}`, background: "transparent", color: "var(--blog-text-secondary)", fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
